import { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

import { fetchPartners } from '@/services/partnersService';

type Partner = {
  id: number;
  imageUrl: string;
  order: number;
};

export default function OurPartners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);

  // Init AOS once
  useEffect(() => {
    AOS.init({ duration: 900, once: true });
  }, []);

  // Load partners on mount
  useEffect(() => {
    async function load() {
      try {
        const data = await fetchPartners();

        // SAFETY: ensure data is an array
        if (Array.isArray(data)) {
          setPartners(data);
        } else {
          console.error('Partner API returned non-array:', data);
          setPartners([]); // fallback
        }
      } catch (e) {
        console.error('Failed to load partners:', e);
        setPartners([]); // fail-safe
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  return (
    <section
      data-aos="fade-up"
      className="bg-white py-16 text-center lg:py-20 2xl:py-24"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 md:px-24 2xl:max-w-[1600px] 2xl:px-40">
        {/* Title */}
        <h2 className="text-2xl font-extrabold text-[#0B0B0B] sm:text-3xl md:text-4xl lg:text-5xl">
          Our Partners
        </h2>

        {/* Subtitle */}
        <p className="mx-auto mt-4 max-w-3xl text-sm text-gray-700 sm:text-base md:text-lg lg:mt-5">
          Innovation thrives through collaboration—working hand in hand with the
          Armed Forces and industry leaders.
        </p>

        {/* Logos Grid */}
        <div className="mt-12 grid grid-cols-2 gap-8 sm:grid-cols-3 md:grid-cols-4 lg:mt-16 lg:grid-cols-5 lg:gap-10 2xl:gap-12">
          {/* Loading Skeletons */}
          {loading
            ? [...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="flex h-24 animate-pulse rounded-lg bg-gray-200 sm:h-28 md:h-32 lg:h-36 xl:h-40 2xl:h-44"
                />
              ))
            : null}

          {/* Partner Logos */}
          {!loading &&
            partners.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-center transition-transform duration-300 hover:scale-105"
              >
                <img
                  src={p.imageUrl}
                  alt={`Partner ${p.id}`}
                  className="h-24 w-auto object-contain sm:h-28 md:h-32 lg:h-36 xl:h-40 2xl:h-44"
                />
              </div>
            ))}

          {/* Empty State (optional) */}
          {!loading && partners.length === 0 && (
            <p className="col-span-full text-gray-500">No partners found.</p>
          )}
        </div>
      </div>
    </section>
  );
}
